/**
 * Intervallo di date con il calendario di shadcn in un popover, fratello di
 * `CampoData`: stesso calendario in UTC, in italiano, dal lunedi.
 *
 * Il bottone mostra l'intervallo in vigore, anche quando non l'ha scelto il
 * campo (per esempio la finestra di un filtro). Il primo tocco fissa un
 * estremo, il secondo l'altro, in qualunque ordine; al secondo il campo
 * consegna "da" e "a" e si chiude.
 */

import { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { it } from 'react-day-picker/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatoGiorno, giornoUtc, oggi } from './date.js';

const formatoCampo = formatoGiorno({ day: 'numeric', month: 'short', year: 'numeric' });

/** "2026-09-23" dalla mezzanotte UTC che il calendario restituisce. */
const perValore = (giorno: Date): string => giorno.toISOString().slice(0, 10);

export function CampoIntervallo({
  id,
  da,
  a,
  onCambia,
}: {
  id: string;
  /** Estremi mostrati, "2026-09-01" e "2026-09-25". */
  da: string;
  a: string;
  onCambia: (da: string, a: string) => void;
}) {
  const [aperto, setAperto] = useState(false);
  /** Primo estremo toccato, in attesa del secondo. */
  const [primo, setPrimo] = useState<Date>();
  const inizio = giornoUtc(da);
  const fine = giornoUtc(a);

  return (
    <Popover
      open={aperto}
      onOpenChange={(v) => {
        setAperto(v);
        setPrimo(undefined);
      }}
    >
      <PopoverTrigger asChild>
        <Button id={id} type="button" variant="outline" className="justify-start font-normal">
          <CalendarIcon data-icon="inline-start" />
          {formatoCampo.format(inizio)} – {formatoCampo.format(fine)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          timeZone="UTC"
          locale={it}
          selected={primo ? { from: primo, to: undefined } : { from: inizio, to: fine }}
          // Oggi e il giorno italiano: a mezzanotte e mezza in UTC e ancora ieri.
          today={giornoUtc(oggi())}
          defaultMonth={fine}
          onSelect={(_, giorno) => {
            if (!primo) {
              setPrimo(giorno);
              return;
            }
            const [x, y] = primo <= giorno ? [primo, giorno] : [giorno, primo];
            onCambia(perValore(x), perValore(y));
            setPrimo(undefined);
            setAperto(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
